import SEOHead from "@/components/SEOHead";
import FaqAccordion from "@/components/FaqAccordion";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import B2BMarketingAgenciesGuideLink from "@/components/B2BMarketingAgenciesGuideLink";
import LocalIndustryGuideLinks from "@/components/LocalIndustryGuideLinks";
import { aiMarketingAgencyLondonSchema } from "@/data/schemaPatchGraphs";
import { AI_MARKETING_AGENCY_LONDON_FAQS } from "@/data/aiMarketingAgencyLondonFaqs";
import { Link } from "wouter";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { AnswerFirstSections } from "@/components/AnswerFirstSections";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SchemaScript from "@/components/SchemaScript";

const faqs = [...AI_MARKETING_AGENCY_LONDON_FAQS];

const capabilities = [
  {
    title: "Buyer and persona research",
    body: "Document the buyer question, evidence needed, decision stage, and commercial owner before choosing channels or tools.",
  },
  {
    title: "AI search and content operations",
    body: "Make approved facts, useful answers, and structured data easier for people and answer systems to read and assess.",
  },
  {
    title: "Paid media and testing",
    body: "Use the same buyer definition in campaigns, landing pages, and controlled tests, with a documented review point.",
  },
  {
    title: "Agent-supported workflows",
    body: "Use agents for bounded research, drafting, and monitoring tasks. Agents draft; a named senior person signs off.",
  },
];

const programStages = [
  {
    title: "Define the decision",
    body: "Agree the buyer, priority question, evidence available, owner, and measure that will inform the next commercial decision.",
  },
  {
    title: "Prepare a bounded release",
    body: "Set the source material, review gate, acceptance criteria, and stop rule before any buyer-facing work is published or funded.",
  },
  {
    title: "Review the evidence",
    body: "Compare repeated observations with CRM and channel evidence, then decide what to keep, revise, or stop with a named senior owner.",
  },
];

export default function ServiceAIMarketingLondon() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="AI Marketing Agency London | B2B AI Growth Marketing"
        description="AI marketing agency in London connecting agentic AI, AI search and paid media to one revenue plan, built from one documented buyer persona per market."
        canonical="https://integrated.social/services/ai-marketing-agency-london/"
      />
      <SchemaScript schema={JSON.stringify(aiMarketingAgencyLondonSchema)} />

      <main>
        <nav className="container pt-24 pb-4" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground">
            <li><Link href="/" className="hover:text-foreground transition-colors">Home</Link></li>
            <li className="opacity-40">/</li>
            <li><Link href="/services/" className="hover:text-foreground transition-colors">Services</Link></li>
            <li className="opacity-40">/</li>
            <li className="text-foreground font-medium">AI Marketing Agency London</li>
          </ol>
        </nav>

        <header className="container pb-16">
          <div className="max-w-4xl">
            <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 rounded-full mb-6">
              London · Agentic AI · AEO · GEO · PPC
            </span>
            <h1 className="font-serif headline-xl mb-6">
              AI Marketing Agency London: Agentic AI, AEO and PPC as One Revenue System
            </h1>
            <p data-answer-block className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed mb-6">
              An AI marketing agency in London should connect agentic AI, AI search and paid media to one revenue plan, not separate projects. Integrated.Social builds each program from a documented buyer persona. Agents draft; a named senior person signs off. Progress is reported against pipeline, not output volume. It suits London B2B teams that want one senior-led plan across channels; start with a Free AI Visibility Score or a 30-minute call with Modi.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                href="/free-ai-growth-audit/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]"
              >
                Check my AI visibility <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="/pricing/"
                className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-lg hover:bg-accent transition-all duration-200"
              >
                Compare the AI Visibility Audit, from £750 including VAT
              </Link>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              Use the <strong className="text-foreground">Free AI Visibility Score</strong> for a self-serve starting view. Use the <Link href="/pricing/" className="text-[#FF8C00] underline-offset-4 hover:underline">AI Visibility Audit, from £750 including VAT (one-off)</Link> when you need a documented paid assessment and a prioritized next-step recommendation. Neither route guarantees a citation, ranking, lead, or revenue outcome.
            </p>
            <p className="mt-3 text-xs text-muted-foreground">Updated November 2, 2026: buyer decision tools and service FAQs reviewed for evidence, scope, and next-step clarity.</p>
          </div>
        </header>

        <AnswerFirstSections
          methodTitle="How the program fits together"
          method={<p>Agents handle bounded research, first drafts, and monitoring. A named senior person signs off the strategy, claims, and spend. Paid and earned work start from the same documented persona, so what the team learns in campaigns can inform approved content and the reverse. Pipeline evidence is reviewed as context for the next decision, not as an automatic verdict.</p>}
          fit={<><p><strong>For:</strong> London B2B teams that want one senior-led plan across agent-supported systems, AI search, and paid media.</p><p className="mt-2"><strong>Also relevant to:</strong> B2C and ecommerce brands that need approved buyer evidence carried consistently between campaigns and content.</p><p className="mt-2"><strong>Not for:</strong> teams seeking an unattended tool installation or content volume without human review.</p></>}
          evidence={<><p><strong>Published example:</strong> The B2B SaaS ABM case study reports +312% qualified pipeline in 90 days and £2.4M pipeline in 6 months, ABM only. The UK FinTech case study documents a persona-led program for a VC-backed app. <Link href="/case-studies/" className="underline underline-offset-4">Read the published case studies</Link>. Published examples do not promise the same outcome elsewhere.</p><p className="mt-3"><strong>Future objective:</strong> A documented buyer plan across channels, reviewed against the evidence available to the team.</p></>}
        />

        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text="AI marketing agency in London. Integrated.Social connects agentic AI, AI search, and paid media to one revenue plan built from a documented buyer persona. Agents draft; a named senior person signs off. Progress is reviewed against pipeline evidence, not output volume."
            title="AI Marketing Agency London | Integrated.Social"
            readingTime={5}
          />
        </div>

        <section className="py-20">
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div>
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">What we do</span>
                <h2 className="font-serif text-3xl md:text-4xl mb-6">AI marketing for London teams that need one senior-led plan</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  AI marketing is not one channel or one tool. It is a way to connect research, content, answer-engine visibility, paid media, and review around the same documented buyer question. The program starts from the evidence available, not from a promise about what an engine will surface.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The (Re)Target framework connects persona research, content architecture, agent-supported workflow, and channel measurement around the same buyer intelligence. It is a planning method, not a guarantee of a commercial outcome.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  The practical aim is a more coherent buyer journey: approved evidence in content, controlled AI-supported work, and paid media reviewed against the data a client can actually verify. Each decision remains subject to the client context and a named senior review.
                </p>
                <Link href="/methodology/" className="inline-flex items-center gap-2 text-sm font-medium text-[#FF8C00] hover:underline">
                  Read the (Re)Target methodology <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {capabilities.map((capability) => (
                  <div key={capability.title} className="border border-border rounded-sm p-5">
                    <h3 className="font-semibold text-sm mb-2">{capability.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{capability.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="pb-20">
          <div className="container">
            <h2 className="font-serif text-3xl md:text-4xl mb-8">Route each capability to the substantive service</h2>
            <div className="overflow-x-auto border border-border rounded-lg">
              <table className="w-full min-w-[680px] text-sm">
                <thead className="bg-accent/40"><tr><th className="px-4 py-3 text-left">Capability</th><th className="px-4 py-3 text-left">Related service</th></tr></thead>
                <tbody className="divide-y divide-border">
                  <tr><td className="px-4 py-4">Agent-supported marketing workflows</td><td className="px-4 py-4"><Link className="text-[#FF8C00] hover:underline" href="/services/gemini-agentic-ai/">Agentic AI marketing</Link></td></tr>
                  <tr><td className="px-4 py-4">AEO and GEO</td><td className="px-4 py-4"><Link className="text-[#FF8C00] hover:underline" href="/services/seo-aeo-geo/">SEO, AEO, and GEO</Link></td></tr>
                  <tr><td className="px-4 py-4">PPC</td><td className="px-4 py-4"><Link className="text-[#FF8C00] hover:underline" href="/services/ppc-performance-max/">PPC and Performance Max</Link></td></tr>
                  <tr><td className="px-4 py-4">AI websites</td><td className="px-4 py-4"><Link className="text-[#FF8C00] hover:underline" href="/services/ai-websites/">AI websites</Link></td></tr>
                  <tr><td className="px-4 py-4">Executive training</td><td className="px-4 py-4"><Link className="text-[#FF8C00] hover:underline" href="/services/ai-training-for-leaders/">AI training for leaders</Link></td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="bg-accent/30 py-20">
          <div className="container max-w-5xl">
            <div className="max-w-3xl mb-10">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Commercial decision tools</span>
              <h2 className="font-serif text-3xl md:text-4xl mb-5">Choose an AI marketing partner on evidence, not theater</h2>
              <p className="text-muted-foreground leading-relaxed">Compare agencies against the same commercial brief: the decision to improve, the evidence available, the people who approve work, the first bounded release, and the measure that would justify a next investment. These tools are practical diligence aids, not a score that predicts commercial outcomes.</p>
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
              <article className="rounded-xl border border-border bg-background p-6">
                <h3 className="font-serif text-xl mb-3">Unified Buyer Evidence Card</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">For each priority question, record the buyer, claim, source, owner, review gate, and next commercial action. If one of those fields is missing, treat the work as a hypothesis rather than proof.</p>
              </article>
              <article className="rounded-xl border border-border bg-background p-6">
                <h3 className="font-serif text-xl mb-3">AI Agent Readiness Checklist</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">Test a narrow workflow before expanding an agent into buyer-facing work. The relevant question is not whether a model can produce text; it is whether the team can govern its inputs and outputs.</p>
              </article>
              <article className="rounded-xl border border-border bg-background p-6">
                <h3 className="font-serif text-xl mb-3">Agency evaluation scorecard</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">Score the approach, not the presentation. Apply the same criteria to every proposal, then ask for one worked prioritization example.</p>
              </article>
            </div>
            <div className="mt-8 flex flex-wrap gap-4 text-sm">
              <Link href="/blog/aeo-agency-what-to-hire-for-2026/" className="font-medium text-[#FF8C00] hover:underline">Use the AEO Agency Evaluation Scorecard <ArrowRight className="inline h-4 w-4" /></Link>
              <Link href="/blog/what-does-an-aeo-agency-uk-do-b2b-buyers-guide-2026/" className="font-medium text-[#FF8C00] hover:underline">Read the UK AEO buyer guide <ArrowRight className="inline h-4 w-4" /></Link>
            </div>
          </div>
        </section>

        <section className="bg-[#0F1419] py-20">
          <div className="container">
            <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Program structure</span>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">A staged AI marketing program</h2>
            <p className="text-white/50 text-lg max-w-2xl mb-12">The scope follows the buyer question, available evidence, review capacity, and agreed decision point. No stage promises a particular ranking, citation, pipeline, or revenue outcome.</p>
            <div className="grid md:grid-cols-3 gap-6">
              {programStages.map((stage, index) => (
                <div key={stage.title} className="border border-white/10 rounded-sm p-6">
                  <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-3">0{index + 1}</span>
                  <h3 className="font-semibold text-white text-lg mb-3">{stage.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{stage.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container">
            <div className="max-w-3xl">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">London context</span>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">What London teams should ask before choosing an AI marketing agency</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">AI-assisted research is changing how buyers compare suppliers. A practical program should define the buyer question, evidence available, human review point, and measure that will decide the next change.</p>
              <p className="text-muted-foreground leading-relaxed mb-6">A useful partner can distinguish what is observable from what is assumed, explain how buyer-facing claims are approved, and show how paid and earned work use the same buyer definition. Agency labels alone do not prove a method or an outcome.</p>
              <div className="flex flex-wrap gap-3">
                <Link href="/case-studies/" className="inline-flex items-center gap-2 text-sm font-medium text-[#FF8C00] hover:underline">Review published case studies <ArrowRight className="w-4 h-4" /></Link>
                <span className="text-muted-foreground text-sm" aria-hidden="true">·</span>
                <Link href="/blog/retarget-framework-b2b-ai-marketing-methodology/" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:underline">Read the (Re)Target Framework <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 border-t border-border">
          <div className="container max-w-5xl">
            <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Buyer checklist</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-6">How to choose an AI marketing agency in London</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">Use the same questions for every prospective partner. The aim is to test the clarity of its method, evidence, and ownership before a buyer-facing program expands.</p>
            <ul className="grid md:grid-cols-2 gap-4">
              {[
                "What buyer decision and source evidence does the proposed work address?",
                "Who reviews factual claims, budget changes, and buyer-facing material?",
                "Can the agency show a bounded test, acceptance criteria, and stop rule?",
                "How will it distinguish repeated observations from one-off screenshots?",
                "How do paid media, content, and answer-ready pages use the same buyer definition?",
                "Which published examples are relevant, and what are their stated limits?",
              ].map((item, index) => (
                <li key={item} className="flex items-start gap-3 text-sm border border-border rounded-sm p-4">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-[#FF8C00]/10 border border-[#FF8C00]/30 flex items-center justify-center text-[#FF8C00] text-xs font-bold mt-0.5">{index + 1}</span>
                  <span className="text-muted-foreground leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="bg-accent/30 py-20" aria-labelledby="ai-marketing-london-faq-heading">
          <div className="container">
            <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">FAQ</span>
            <h2 id="ai-marketing-london-faq-heading" className="font-serif text-3xl md:text-4xl mb-4">AI Marketing Agency London: frequently asked questions</h2>
            <p data-faq-summary className="max-w-3xl text-muted-foreground leading-relaxed mb-10">These answers cover what an AI marketing agency in London should do, why AI visibility is not fully automatic, how agents and people share the work, how progress is measured, and what to ask before you hire. In short, one senior-led plan joins agentic AI, AI search, and paid media around one buyer persona, a named senior person signs off every claim, and progress is reported against pipeline.</p>
            <div className="max-w-3xl space-y-3">
              <FaqAccordion
                items={faqs}
                className="space-y-3"
                itemClassName="border border-border rounded-sm overflow-hidden"
                buttonClassName="w-full text-left px-6 py-4 flex items-center justify-between gap-4 hover:bg-accent/50 transition-colors"
                questionClassName="m-0"
                answerClassName="px-6 py-4 text-muted-foreground text-sm leading-relaxed"
                iconClassName="w-5 h-5 shrink-0 text-muted-foreground transition-transform duration-200"
              />
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container">
            <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-6">Related services and resources</span>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { href: "/services/gemini-agentic-ai/", label: "Agentic AI Marketing", desc: "Multi-agent systems for bounded GTM, content, and campaign workflows with human review." },
                { href: "/services/seo-aeo-geo/", label: "SEO, AEO, and GEO", desc: "Crawlable pages, clear entity facts, and answer-first content for AI search readiness." },
                { href: "/services/ppc-performance-max/", label: "PPC and Performance Max", desc: "Paid media planning and measurement around a documented buyer persona." },
                { href: "/case-studies/", label: "Client case studies", desc: "Published ABM and persona-led examples, with their stated scope and limitations." },
                { href: "/blog/retarget-framework-b2b-ai-marketing-methodology/", label: "The (Re)Target Framework", desc: "A planning framework for buyer personas, content, paid media, and review." },
                { href: "/modi-elnadi/", label: "Modi Elnadi", desc: "Founder and Director of AI Growth and AEO at Integrated.Social." },
                { href: "/pricing/#ai-visibility-audit", label: "AI Visibility Audit", desc: "A senior-led diagnostic from £750 including VAT (one-off), with documented scope and next-step guidance." },
                { href: "/services/account-based-marketing/#fintech", label: "FinTech marketing support", desc: "ABM, AI search, and paid-media context for B2B finance teams, with client approval retained." },
              ].map((link) => (
                <Link key={link.href} href={link.href} className="group block border border-border rounded-sm p-5 hover:bg-accent/50 transition-colors">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className="font-medium text-sm group-hover:text-[#FF8C00] transition-colors">{link.label}</span>
                    <ArrowRight className="w-4 h-4 shrink-0 text-muted-foreground group-hover:text-[#FF8C00] transition-colors mt-0.5" />
                  </div>
                  <p className="text-muted-foreground text-xs leading-relaxed">{link.desc}</p>
                </Link>
              ))}
            </div>
            <B2BMarketingAgenciesGuideLink headingLevel="p" />
            <LocalIndustryGuideLinks context="general" />
          </div>
        </section>

        <section id="get-started" className="py-20 lg:py-28 bg-[#0F1419]" aria-labelledby="ai-marketing-london-cta-heading">
          <div className="container">
            <div className="max-w-2xl mx-auto text-center">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Start here</span>
              <h2 id="ai-marketing-london-cta-heading" className="font-serif text-3xl md:text-4xl text-white mb-4">Check the current evidence before choosing a next step</h2>
              <p className="text-white/50 text-lg mb-8">The Free AI Visibility Score provides a high-level baseline across broad AI visibility dimensions. It is separate from the paid AI Visibility Audit, from £750 including VAT (one-off), and does not provide a fix-by-fix remediation plan.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/free-ai-growth-audit/" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]">Check my AI visibility <ArrowRight className="w-4 h-4" /></Link>
                <Link href="/services/ai-marketing-strategy/" className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white font-medium rounded-lg hover:bg-white/5 transition-all duration-200">View AI Marketing Strategy service</Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 border-t border-border">
          <div className="container">
            <div className="max-w-2xl">
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-4">About the author</span>
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-full bg-[#FF8C00]/10 flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-[#FF8C00]" /></div>
                <div>
                  <Link href="/modi-elnadi/" className="font-semibold hover:underline">Modi Elnadi</Link>
                  <p className="text-muted-foreground text-xs mb-3">Founder and Director of AI Growth and AEO, Integrated.Social</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">Modi Elnadi has a design background, an MBA, and 20+ years in digital marketing, with £20m+ in paid media managed. Agents draft; a named senior person signs off the strategy, claims, and buyer-facing work.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
      </main>
      <ArabicToggle />
    </div>
  );
}
